import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    IconButton,
    Button,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Badge,
    Avatar,
    useTheme,
    useMediaQuery,
    Menu,
    MenuItem,
    Divider,
    Dialog,
    DialogContent,
    Collapse,
} from '@mui/material';
import {
    Close,
} from '@mui/icons-material';
import LanguageSelector from '../../language/LanguageSelector';
import { useUser } from '../../context/UserContext';
import {
    IconMenu2,
    IconChevronLeft,
    IconChevronRight,
    IconBolt,
    IconPlus,
    IconDashboard,
    IconTelescope,
    IconPlant,
    IconLeaf,
    IconChartLine,
    IconBuildingBank,
    IconChevronDown,
    IconUser,
    IconSettings,
    IconX,
    IconHeart,
    IconLogout,
    IconLogin,
    IconShieldCheck,
    IconInfoCircle,
    IconHelp,
    IconHeadset,
    IconEye,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { colors, fonts } from '../../ColorFont/color';
import { auth } from '../../auth/firebase-config/firebase';
import { signOut } from 'firebase/auth';
import SignUp from '../../auth/SignUp';
import { admin } from '../../ConstUrl/Url';

const layoutTranslations = {
    en: {
        dashboard: "Dashboard",
        explore: "Explore",
        cropListing: "Crop Listing",
        carbonCredits: "Carbon Credits",
        subscription: "Plans",
        settings: "Settings",
        add: "Add",
        boost: "2/15 boost hr",
        overview: "OVERVIEW",
        profile: "Profile",
        logout: "Logout",
        login: "Login",
        loggedInAs: "Logged in as",
        wishlist: "Wishlist",
        policiesLegal: "Policies & Legal",
        termsAndConditions: "Terms & Conditions",
        privacyPolicy: "Privacy Policy",
        refundPolicy: "Refund Policy",
        shippingPolicy: "Shipping Policy",
        cancellationPolicy: "Cancellation Policy",
        aboutUs: "About Us",
        faqs: "FAQs",
        support: "Support",
    },
    hi: {
        dashboard: "डैशबोर्ड",
        explore: "खोजें",
        cropListing: "फसल सूची",
        carbonCredits: "कार्बन क्रेडिट्स",
        subscription: "प्लान",
        settings: "सेटिंग्स",
        add: "जोड़ें",
        boost: "2/15 बूस्ट घंटे",
        overview: "अवलोकन",
        profile: "प्रोफ़ाइल",
        logout: "लॉग आउट",
        login: "लॉग इन",
        loggedInAs: "लॉग इन है",
        wishlist: "इच्छा सूची",
        policiesLegal: "नीतियाँ और कानूनी",
        termsAndConditions: "नियम और शर्तें",
        privacyPolicy: "गोपनीयता नीति",
        refundPolicy: "वापसी नीति",
        shippingPolicy: "शिपिंग नीति",
        cancellationPolicy: "रद्द करने की नीति",
        aboutUs: "हमारे बारे में",
        faqs: "सामान्य प्रश्न",
        support: "सहयोग",
    },
    mr: {
        dashboard: "डॅशबोर्ड",
        explore: "अन्वेषण करा",
        cropListing: "पीक सूची",
        carbonCredits: "कार्बन क्रेडिट्स",
        subscription: "प्लॅन",
        settings: "सेटिंग्ज",
        add: "जोडा",
        boost: "2/15 बूस्ट तास",
        overview: "आढावा",
        profile: "प्रोफाइल",
        logout: "लॉग आउट",
        login: "लॉग इन",
        loggedInAs: "लॉग इन आहे",
        wishlist: "आवडते",
        policiesLegal: "धोरणे आणि कायदेशीर",
        termsAndConditions: "अटी आणि शर्ती",
        privacyPolicy: "गोपनीयता धोरण",
        refundPolicy: "परतावा धोरण",
        shippingPolicy: "शिपिंग धोरण",
        cancellationPolicy: "रद्द करण्याचे धोरण",
        aboutUs: "आमच्याबद्दल",
        faqs: "वारंवार विचारले जाणारे प्रश्न",
        support: "आधार",
    }
};

const SIDEBAR_WIDTH = 216;
const COLLAPSED_WIDTH = 80;
// PRIMARY is now used directly from colors.primary inside the component to ensure reactivity

const NewHeaderNavWithSidebar = React.memo((props) => {
    const { children, headerTitle, activeSegment, onNavItemClick } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation(['common', 'dashboard']);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const smMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(!isMobile);
    const [loginOpen, setLoginOpen] = useState(false); // State for login dialog
    const [isRoleSelectionActive, setIsRoleSelectionActive] = useState(false); 

    const { firebaseUser, userData: localUser, isLoggedIn } = useUser();
    const isAdmin = firebaseUser?.email === admin;

    useEffect(() => {
        // Check for ?openLogin=true in URL
        const params = new URLSearchParams(window.location.search);
        if (params.get('openLogin') === 'true') {
            setLoginOpen(true);
            // Clean up the URL
            const url = new URL(window.location);
            url.searchParams.delete('openLogin');
            window.history.replaceState({}, '', url);
        }
    }, []);

    // Reset scroll on route change for the inner content container
    useEffect(() => {
        const contentArea = document.getElementById('main-content-area');
        if (contentArea) {
            contentArea.scrollTo(0, 0);
        }
    }, [location.pathname]);

    const dbName = localUser?.firstName && localUser?.lastName 
        ? `${localUser.firstName} ${localUser.lastName}`
        : localUser?.firstName || localUser?.lastName || localUser?.fullName || localUser?.name || localUser?.username;

    const displayName =
        dbName ||
        firebaseUser?.displayName ||
        (firebaseUser?.email ? firebaseUser.email.split('@')[0] : null) ||
        'User';
    const displayEmail = firebaseUser?.email || localUser?.email || '';
    
    // Role Translation Helper
    const translateRole = (role) => {
        if (!role) return '';
        const lowerRole = role.toLowerCase();
        // Check for common variations
        if (lowerRole === 'buyer' || lowerRole === 'supplier') return t('common:buyer');
        if (lowerRole === 'farmer' || lowerRole === 'seller') return t('common:farmer');
        if (lowerRole === 'admin') return t('common:admin', 'Admin');
        return t(`common:${lowerRole}`, role);
    };

    const displayRole = translateRole(localUser?.role);
    const rawRole = localUser?.role?.toLowerCase() || '';
    const isBuyerRole = rawRole === 'buyer' || rawRole === 'supplier';
    const displayAvatar = firebaseUser?.photoURL || localUser?.photoURL || null;

    // ─── Profile dropdown ─────────────────────────────────────────────────────
    const [profileAnchor, setProfileAnchor] = useState(null);
    const openProfile = Boolean(profileAnchor);

    const handleProfileClick = (e) => setProfileAnchor(e.currentTarget);
    const handleProfileClose = () => setProfileAnchor(null);

    const handleSignOut = async () => {
        handleProfileClose();
        try {
            await signOut(auth);
            localStorage.removeItem('userData');
            localStorage.removeItem('userRole');
            // Immediately show the login dialog — this IS the login screen
            setLoginOpen(true);
        } catch (err) {
            console.error('Sign out error:', err);
        }
    };

    const handleLoginClick = () => {
        handleProfileClose();
        // window.location.href = '/login'; // Commented out
        setLoginOpen(true); // Same as HeaderNav.jsx
    };

    // ─── Sidebar content ──────────────────────────────────────────────────────
    const toggleSidebar = () => setOpen(!open);

    const [policiesOpen, setPoliciesOpen] = useState(false);

    const navItems = [
        { title: t('dashboard:dashboard'), icon: <IconDashboard size={24} />, path: '/dashboard', segment: '/dashboard' },
        { title: t('dashboard:explore'), icon: <IconTelescope size={24} />, path: '/explore', segment: '/explore' }, 
        ...(isAdmin ? [
            { title: t('dashboard:list-buyers'), icon: <IconUser size={24} />, path: '/list-buyers', segment: '/list-buyers' },
            { title: t('dashboard:sellers-list'), icon: <IconUser size={24} />, path: '/list-sellers', segment: '/list-sellers' },
            { title: t('dashboard:list-all-plots'), icon: <IconDashboard size={24} />, path: '/list-all-plots', segment: '/list-all-plots' },
            { title: t('dashboard:manage-categories'), icon: <IconPlant size={24} />, path: '/manage-crops', segment: '/manage-crops' },
            { title: t('dashboard:manage-plans'), icon: <IconChartLine size={24} />, path: '/manage-plans', segment: '/manage-plans' },
            { title: t('dashboard:verify-carbon-proofs'), icon: <IconLeaf size={24} />, path: '/manage-carbon-proofs', segment: '/manage-carbon-proofs' },
            { title: t('dashboard:manage-dashboard'), icon: <IconDashboard size={24} />, path: '/manage-dashboard', segment: '/manage-dashboard' },
        ] : isBuyerRole ? [
            /* BUYER / SUPPLIER SPECIFIC ITEMS */
            { title: t('dashboard:wishlist'), icon: <IconHeart size={24} />, path: "/wishlist", segment: "/wishlist" },
            { title: t('dashboard:viewedPlots'), icon: <IconEye size={24} />, path: "/viewed-plots", segment: "/viewed-plots" },
            { title: t('dashboard:subscription'), icon: <IconChartLine size={24} />, path: '/buyer/boost', segment: '/buyer/boost' },
        ] : [
            /* FARMER / SELLER SPECIFIC ITEMS */
            { title: t('dashboard:wishlist'), icon: <IconHeart size={24} />, path: "/wishlist", segment: "/wishlist" },
            { title: t('dashboard:myListings'), icon: <IconPlant size={24} />, path: '/my-listings', segment: '/my-listings' },
            { title: t('dashboard:carbonCredits'), icon: <IconLeaf size={24} />, path: '/carbon-credits', segment: '/carbon-credits' },
            { title: t('dashboard:subscription'), icon: <IconChartLine size={24} />, path: '/farmer/boost', segment: '/farmer/boost' },
        ]),
        { 
            title: t('dashboard:policiesLegal'), 
            icon: <IconShieldCheck size={24} />, 
            isSubmenu: true,
            open: policiesOpen,
            setOpen: setPoliciesOpen,
            children: [
                { title: t('dashboard:termsAndConditions'), path: "/terms-conditions", segment: "/terms-conditions" },
                { title: t('dashboard:privacyPolicy'), path: "/privacy-policy", segment: "/privacy-policy" },
                { title: t('dashboard:refundPolicy'), path: "/refund-policy", segment: "/refund-policy" },
                { title: t('dashboard:shippingPolicy'), path: "/shipping-policy", segment: "/shipping-policy" },
                { title: t('dashboard:cancellationPolicy'), path: "/cancellation-policy", segment: "/cancellation-policy" },
            ]
        },
        { title: t('dashboard:aboutUs'), icon: <IconInfoCircle size={24} />, path: "/about", segment: "/about" },
        { title: t('dashboard:faqs'), icon: <IconHelp size={24} />, path: "/faqs", segment: "/faqs" },
        { title: t('dashboard:support'), icon: <IconHeadset size={24} />, path: "/support", segment: "/support" },
        { title: t('dashboard:settings'), icon: <IconSettings size={24} />, path: '/settings', segment: '/settings' },
    ];

    const sidebarContent = (
        <Box sx={{
            height: '100%',
            width: open ? SIDEBAR_WIDTH : COLLAPSED_WIDTH,
            transition: 'width 0.3s ease',
            borderRight: `1px dashed ${colors.layout_border || '#919EAB33'}`,
            backgroundColor: colors.layout_bg || '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'visible'
        }}>
            {/* Logo Area */}
            <Box sx={{
                height: { xs: '80px', md: '90px' },
                display: 'flex',
                alignItems: 'center',
                px: open ? 2 : 1,
                justifyContent: open ? 'flex-start' : 'center',
                position: 'relative'
            }}>
                <Box
                    onClick={() => navigate('/')}
                    component="img"
                    src="/Icon krushisetu.png"
                    alt="Krushisetu Logo"
                    sx={{
                        width: open ? 38 : 34,
                        height: 'auto',
                        cursor: 'pointer',
                        mr: open ? 1 : 0,
                        objectFit: 'contain',
                        mixBlendMode: 'multiply'
                    }}
                />
                {open && (
                    <Typography
                        variant="h6"
                        onClick={() => navigate('/')}
                        sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            color: '#1C252E',
                            cursor: 'pointer',
                            fontSize: '18px',
                            letterSpacing: '-0.5px',
                            ml: 0.5,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden'
                        }}
                    >
                        {t('dashboard:krushiSetu')}
                    </Typography>
                )}
            </Box>

            {/* Toggle Button for Desktop */}
            {!isMobile && (
                <IconButton
                    onClick={toggleSidebar}
                    sx={{
                        position: 'absolute',
                        top: '32px',
                        right: '-18px',
                        width: '36px',
                        height: '36px',
                        backgroundColor: '#FFFFFF',
                        border: `1px solid rgba(145, 158, 171, 0.24)`,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0px 8px 20px rgba(0,0,0,0.15)',
                        zIndex: 11,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { 
                            backgroundColor: colors.primary_light || '#F4F6F8',
                            transform: 'scale(1.1)',
                            border: `1px solid ${colors.primary}`,
                            boxShadow: '0px 10px 24px rgba(0,0,0,0.2)'
                        }
                    }}
                >
                    {open ? (
                        <IconChevronLeft size={22} color={colors.primary_dark || "#1C252E"} stroke={3} />
                    ) : (
                        <IconChevronRight size={22} color={colors.primary_dark || "#1C252E"} stroke={3} />
                    )}
                </IconButton>
            )}

            {/* Nav List - Scrollable Area */}
            <Box sx={{ 
                flexGrow: 1, 
                overflowY: 'auto',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': {
                    width: '5px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(145, 158, 171, 0.24)',
                    borderRadius: '10px',
                    '&:hover': {
                        backgroundColor: 'rgba(145, 158, 171, 0.48)',
                    }
                }
            }}>
                <List sx={{ px: open ? 2 : 1, mt: 2 }}>
                    {open && (
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#919EAB',
                            mb: 2,
                            px: 2,
                            letterSpacing: '1px'
                        }}>
                            {t('dashboard:overview')}
                        </Typography>
                    )}

                    {navItems.map((item, index) => {
                        if (item.isSubmenu) {
                            return (
                                <React.Fragment key={index}>
                                    <ListItem
                                        button
                                        onClick={() => item.setOpen(!item.open)}
                                        sx={{
                                            borderRadius: '8px',
                                            mb: 1,
                                            justifyContent: open ? 'flex-start' : 'center',
                                            px: open ? 1.5 : 1,
                                            py: 1,
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            '&:hover': { backgroundColor: colors.primary_rgba_12 }
                                        }}
                                    >
                                        <ListItemIcon sx={{
                                            minWidth: open ? '36px' : 'auto',
                                            color: '#637381'
                                        }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        {open && (
                                            <>
                                                <ListItemText
                                                    primary={item.title}
                                                    primaryTypographyProps={{
                                                        fontFamily: fonts.publicSans || fonts.poppins,
                                                        fontWeight: 500,
                                                        fontSize: '14px',
                                                        color: '#637381'
                                                    }}
                                                />
                                                {item.open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                                            </>
                                        )}
                                    </ListItem>
                                    <Collapse in={item.open && open} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding sx={{ pl: open ? 2 : 0 }}>
                                            {item.children.map((subItem, subIndex) => {
                                                const isSubActive = activeSegment 
                                                    ? (activeSegment === subItem.segment)
                                                    : (location.pathname === subItem.path);

                                                return (
                                                    <ListItem
                                                        button
                                                        key={subIndex}
                                                        onClick={() => {
                                                            if (onNavItemClick) {
                                                                onNavItemClick(subItem);
                                                            } else {
                                                                navigate(subItem.path);
                                                            }
                                                        }}
                                                        sx={{
                                                            borderRadius: '8px',
                                                            mb: 0.5,
                                                            backgroundColor: isSubActive ? colors.primary_rgba_8 : 'transparent',
                                                            justifyContent: open ? 'flex-start' : 'center',
                                                            px: open ? 1.5 : 1,
                                                            py: 0.8,
                                                            cursor: 'pointer',
                                                            userSelect: 'none',
                                                            '&:hover': { backgroundColor: colors.primary_rgba_12 }
                                                        }}
                                                    >
                                                        {open && (
                                                            <ListItemText
                                                                primary={subItem.title}
                                                                primaryTypographyProps={{
                                                                    fontFamily: fonts.publicSans || fonts.poppins,
                                                                    fontWeight: isSubActive ? 600 : 500,
                                                                    fontSize: '13px',
                                                                    color: isSubActive ? colors.primary : '#637381'
                                                                }}
                                                            />
                                                        )}
                                                    </ListItem>
                                                );
                                            })}
                                        </List>
                                    </Collapse>
                                </React.Fragment>
                            );
                        }

                        const isActive = activeSegment 
                            ? (activeSegment === item.segment)
                            : (location.pathname === item.path);

                        return (
                            <ListItem
                                button
                                key={index}
                                onClick={() => {
                                    if (onNavItemClick) {
                                        onNavItemClick(item);
                                    } else {
                                        navigate(item.path);
                                    }
                                }}
                                sx={{
                                    borderRadius: '8px',
                                    mb: 1,
                                    backgroundColor: isActive ? colors.primary_rgba_8 : 'transparent',
                                    justifyContent: open ? 'flex-start' : 'center',
                                    px: open ? 1.5 : 1,
                                    py: 1,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    '&:hover': { backgroundColor: colors.primary_rgba_12 }
                                }}
                            >
                                <ListItemIcon sx={{
                                    minWidth: open ? '36px' : 'auto',
                                    color: isActive ? colors.primary : '#637381'
                                }}>
                                    {item.icon}
                                </ListItemIcon>
                                {open && (
                                    <ListItemText
                                        primary={item.title}
                                        primaryTypographyProps={{
                                            fontFamily: fonts.publicSans || fonts.poppins,
                                            fontWeight: isActive ? 600 : 500,
                                            fontSize: '14px',
                                            color: isActive ? colors.primary : '#637381'
                                        }}
                                    />
                                )}
                            </ListItem>
                        );
                    })}
                </List>
            </Box>

            {/* Sidebar Footer — user info */}
            {open && isLoggedIn && (
                <Box 
                    onClick={() => {
                        navigate('/profile');
                    }}
                    sx={{
                        mt: 'auto',
                        p: 2,
                        borderTop: `1px dashed ${colors.layout_border || '#919EAB33'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' }
                    }}
                >
                    <Avatar
                        src={displayAvatar || undefined}
                        sx={{ width: 36, height: 36, background: colors.primary, fontSize: '15px', fontWeight: 700 }}
                    >
                        {!displayAvatar ? displayName.charAt(0).toUpperCase() : null}
                    </Avatar>
                    <Box sx={{ overflow: 'hidden' }}>
                        <Typography sx={{
                            fontFamily: fonts.poppins,
                            fontWeight: 600,
                            fontSize: '13px',
                            color: '#1C252E',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {displayName}
                        </Typography>
                        {displayRole && (
                            <Typography sx={{
                                fontFamily: fonts.poppins,
                                fontSize: '11px',
                                color: colors.primary,
                                fontWeight: 600
                            }}>
                                {displayRole}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden', backgroundColor: '#FFFFFF' }}>

            {/* Sidebar for Desktop */}
            {!isMobile && (
                <Box sx={{ 
                    width: open ? SIDEBAR_WIDTH : COLLAPSED_WIDTH, 
                    flexShrink: 0, 
                    transition: 'width 0.3s ease',
                    overflow: 'visible',
                    position: 'relative'
                }}>
                    {sidebarContent}
                </Box>
            )}

            {/* Sidebar Drawer for Mobile */}
            {isMobile && (
                <Drawer
                    open={open}
                    onClose={toggleSidebar}
                    variant="temporary"
                    sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, borderRight: 'none' } }}
                >
                    {sidebarContent}
                </Drawer>
            )}

            {/* Main Content Area */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* Header */}
                <Box sx={{
                    height: { xs: '88px', md: '100px' },
                    backgroundColor: colors.layout_bg || '#FFFFFF',
                    borderBottom: `1px solid ${colors.layout_border || '#919EAB33'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: { xs: colors.layout_padding_x_mobile || '16px', md: colors.layout_padding_x || '24px' },
                    py: { xs: 1.5, md: 2 }
                }}>
                    {/* Header Left */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isMobile && (
                            <IconButton 
                                onClick={toggleSidebar} 
                                sx={{ 
                                    mr: 2,
                                    backgroundColor: '#FFFFFF',
                                    boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                                    border: `1px solid rgba(145, 158, 171, 0.12)`,
                                    '&:hover': { backgroundColor: '#F4F6F8' }
                                }}
                            >
                                <IconMenu2 size={24} color="#1C252E" stroke={2.5} />
                            </IconButton>
                        )}
                        {isMobile && headerTitle && (
                            <Typography sx={{
                                fontFamily: fonts.poppins,
                                fontWeight: 700,
                                fontSize: '18px',
                                color: colors.layout_text_primary || '#1C252E'
                            }}>
                                {headerTitle}
                            </Typography>
                        )}
                    </Box>

                    {/* Header Right */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 } }}>

                        {/* Boost Tag — Dynamic based on role */}
                        {!isMobile && isLoggedIn && !isAdmin && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                backgroundColor: isBuyerRole ? '#007867' : '#B71D18', 
                                borderRadius: '50px',
                                pl: 2,
                                pr: '6px',
                                py: '4px',
                                height: '40px',
                                boxShadow: `0px 4px 12px ${isBuyerRole ? 'rgba(0, 120, 103, 0.2)' : 'rgba(183, 29, 24, 0.2)'}`
                            }}>
                                <IconBolt size={20} stroke={2.5} color="#FFFFFF" fill="#FFFFFF" />
                                <Typography sx={{
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    color: '#FFFFFF',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {isBuyerRole 
                                        ? `${localUser?.noOfContacts || 0} Contacts` 
                                        : `${localUser?.sponseredHours || 0} ${t('common:boost')} hr`}
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        if (isBuyerRole) {
                                            navigate('/buyer/boost');
                                        } else {
                                            navigate('/farmer/boost');
                                        }
                                    }}
                                    sx={{
                                        backgroundColor: '#FFFFFF',
                                        color: isBuyerRole ? '#007867' : '#B71D18',
                                        borderRadius: '50px',
                                        height: '28px',
                                        minWidth: '0',
                                        px: 1.5,
                                        py: 0,
                                        textTransform: 'none',
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontWeight: 800,
                                        fontSize: '11px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        boxShadow: 'none',
                                        '&:hover': { backgroundColor: '#F4F6F8', boxShadow: 'none' }
                                    }}
                                >
                                    <Box sx={{ 
                                        width: '16px', height: '16px', borderRadius: '50%', 
                                        backgroundColor: isBuyerRole ? '#007867' : '#B71D18', 
                                        display: 'flex', alignItems: 'center', justifyContent: 'center' 
                                    }}>
                                        <IconPlus size={10} stroke={4} color="#FFFFFF" />
                                    </Box>
                                    {isBuyerRole ? t('dashboard:addContacts') : t('common:sellCrop')}
                                </Button>
                            </Box>
                        )}

                        {/* Language Selector — Tabler icon dropdown */}
                        <LanguageSelector />

                        {/* ── Profile / Login Button ── */}
                        {isLoggedIn ? (
                            /* LOGGED IN — Exactly as in image */
                            <Box
                                onClick={handleProfileClick}
                                sx={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', userSelect: 'none', ml: 1, mr: { xs: 0, md: 1 } }}
                            >
                                <Box sx={{
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '50%',
                                    border: `2px solid ${colors.primary}`, // Dynamic primary border
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: '2px'
                                }}>
                                    <Avatar
                                        src={displayAvatar || undefined}
                                        sx={{ 
                                            width: '34px', 
                                            height: '34px', 
                                            fontSize: '16px', 
                                            fontWeight: 700, 
                                            background: colors.primary_rgba_8,
                                            color: colors.primary
                                        }}
                                    >
                                        {!displayAvatar ? displayName.charAt(0).toUpperCase() : null}
                                    </Avatar>
                                </Box>

                                {!isMobile && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Typography sx={{
                                            fontFamily: fonts.publicSans || fonts.poppins,
                                            fontWeight: 600,
                                            fontSize: '15px',
                                            color: '#1C252E',
                                        }}>
                                            {displayName}
                                        </Typography>
                                        <IconChevronDown size={20} color="#637381" style={{
                                            transform: openProfile ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s'
                                        }} />
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            /* NOT LOGGED IN — show Login button */
                            <Button
                                variant="contained"
                                onClick={handleLoginClick}
                                startIcon={<IconLogin size={18} />}
                                sx={{
                                    backgroundColor: colors.primary,
                                    color: '#FFF',
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    px: 2.5,
                                    mr: { xs: 0, md: 1 },
                                    boxShadow: 'none',
                                    '&:hover': { backgroundColor: colors.primary_dark, boxShadow: 'none' }
                                }}
                            >
                                {t('common:login')}
                            </Button>
                        )}

                        {/* ── Profile Dropdown Menu ── */}
                        <Menu
                            anchorEl={profileAnchor}
                            open={openProfile}
                            onClose={handleProfileClose}
                            PaperProps={{
                                sx: {
                                    mt: 1.5,
                                    minWidth: 220,
                                    borderRadius: '12px',
                                    boxShadow: '0px 12px 32px rgba(0,0,0,0.12)',
                                    border: '1px solid rgba(145,158,171,0.12)',
                                    overflow: 'visible',
                                    backgroundColor: '#FFFFFF',
                                    '&:before': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        top: -8,
                                        right: 20,
                                        width: 16,
                                        height: 16,
                                        bgcolor: '#FFFFFF',
                                        transform: 'rotate(45deg)',
                                        zIndex: 0,
                                        border: '1px solid rgba(145,158,171,0.12)',
                                        borderRight: 'none',
                                        borderBottom: 'none',
                                    }
                                }
                            }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            {isLoggedIn && (
                                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px dashed rgba(145,158,171,0.24)' }}>
                                    <Typography sx={{
                                        fontFamily: fonts.poppins,
                                        fontWeight: 700,
                                        fontSize: '14px',
                                        color: '#1C252E'
                                    }}>
                                        {displayName}
                                    </Typography>
                                    {displayEmail && (
                                        <Typography sx={{
                                            fontFamily: fonts.poppins,
                                            fontSize: '12px',
                                            color: '#637381',
                                            mt: 0.3
                                        }}>
                                            {displayEmail}
                                        </Typography>
                                    )}
                                    {displayRole && (
                                        <Box sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            mt: 0.8,
                                            px: 1,
                                            py: 0.3,
                                            borderRadius: '6px',
                                            backgroundColor: colors.primary_rgba_8
                                        }}>
                                            <Typography sx={{
                                                fontFamily: fonts.poppins,
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                color: colors.primary
                                            }}>
                                                {displayRole}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {isLoggedIn && [
                                {
                                    icon: <IconUser size={18} color="#637381" />,
                                    label: t('dashboard:profile'),
                                    onClick: () => { 
                                        handleProfileClose(); 
                                        if (props.userProfilePage) {
                                            props.userProfilePage();
                                        } else {
                                            navigate('/profile');
                                        }
                                    }
                                },
                                {
                                    icon: <IconSettings size={18} color="#637381" />,
                                    label: t('dashboard:settings'),
                                    onClick: () => { 
                                        handleProfileClose(); 
                                        if (props.userSupporPage) {
                                            props.userSupporPage();
                                        } else {
                                            navigate('/settings');
                                        }
                                    }
                                },
                                {
                                    icon: <IconHeart size={18} color="#637381" />,
                                    label: t('dashboard:wishlist'),
                                    onClick: () => { 
                                        handleProfileClose(); 
                                        if (props.onWishlistClick) {
                                            props.onWishlistClick();
                                        } else {
                                            navigate('/wishlist');
                                        }
                                    }
                                },
                            ].map((item, idx) => (
                                <MenuItem
                                    key={idx}
                                    onClick={item.onClick}
                                    sx={{
                                        px: 2,
                                        py: 1.2,
                                        gap: 1.5,
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontSize: '14px',
                                        color: '#1C252E',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': { backgroundColor: colors.primary_rgba_8 }
                                    }}
                                >
                                    {item.icon}
                                    {item.label}
                                </MenuItem>
                            ))}

                            {isLoggedIn && (
                                <>
                                    <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />
                                    <MenuItem
                                        onClick={handleSignOut}
                                        sx={{
                                            px: 2,
                                            py: 1.2,
                                            gap: 1.5,
                                            fontFamily: fonts.publicSans || fonts.poppins,
                                            fontSize: '14px',
                                            color: '#FF5630',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            '&:hover': { backgroundColor: 'rgba(255,86,48,0.06)' }
                                        }}
                                    >
                                        <IconLogout size={18} color="#FF5630" />
                                        {t('common:logout')}
                                    </MenuItem>
                                </>
                            )}

                            {!isLoggedIn && (
                                <MenuItem
                                    onClick={handleLoginClick}
                                    sx={{
                                        px: 2,
                                        py: 1.2,
                                        gap: 1.5,
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontSize: '14px',
                                        color: colors.primary,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': { backgroundColor: colors.primary_rgba_8 }
                                    }}
                                >
                                    <IconLogin size={18} color={colors.primary} />
                                    {t('common:login')}
                                </MenuItem>
                            )}
                        </Menu>

                    </Box>
                </Box>

                {/* Page Children Content */}
                <Box 
                    id="main-content-area"
                    sx={{ 
                        flexGrow: 1, 
                        overflowY: 'auto',
                        padding: { xs: colors.layout_padding_x_mobile, md: `${colors.layout_padding_y} ${colors.layout_padding_x}` },
                        maxWidth: colors.layout_max_width,
                        margin: colors.layout_margin_center,
                        width: '100%',
                    }}
                >
                    {children}
                </Box>

            </Box>

            {/* Login Dialog Pop-up — Same logic and Figma style as HeaderNav.jsx */}
            <Dialog
                open={loginOpen}
                onClose={() => setLoginOpen(false)}
                fullScreen
                PaperProps={{
                    sx: {
                        backgroundColor: "#FCFDFC",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        position: "relative",
                        overflow: "auto",
                        py: { xs: 4, sm: '3vh' },
                    },
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={() => setLoginOpen(false)}
                    sx={{
                        position: "fixed",
                        right: 16,
                        top: 16,
                        zIndex: 10,
                        color: colors.text || "#6D786D",
                        backgroundColor: "rgba(255,255,255,0.8)",
                        backdropFilter: "blur(4px)",
                        "&:hover": { backgroundColor: "rgba(255,255,255,0.95)" },
                    }}
                >
                    <Close />
                </IconButton>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: {
                            xs: "100%",
                            sm: isRoleSelectionActive ? "32rem" : "28rem"
                        },
                        maxWidth: '100%',
                        px: { xs: '1rem', sm: 0 },
                        my: "auto",
                        transition: "all 0.3s ease-in-out",
                    }}
                >
                    {!isRoleSelectionActive && (
                        <>
                            <Box
                                component="img"
                                src="/Icon krushisetu.png"
                                alt="KrushiSetu Logo"
                                sx={{
                                    width: "clamp(4rem, 8vh, 5rem)",
                                    height: "clamp(4rem, 8vh, 5rem)",
                                    objectFit: "contain",
                                    mixBlendMode: 'multiply'
                                }}
                            />

                            <Typography
                                sx={{
                                    fontFamily: fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: colors.size_h1 || "30px",
                                    lineHeight: 1.2,
                                    letterSpacing: "-0.046rem",
                                    color: colors.text_dark || "#171C17",
                                    textAlign: "center",
                                    mt: "clamp(0.5rem, 2vh, 1rem)",
                                }}
                            >
                                KrushiSetu
                            </Typography>

                            <Typography
                                sx={{
                                    width: "100%",
                                    maxWidth: "17.5rem",
                                    fontFamily: fonts.poppins,
                                    fontWeight: 400,
                                    fontSize: colors.size_body || "16px",
                                    lineHeight: 1.5,
                                    color: colors.text || "#6D786D",
                                    textAlign: "center",
                                    mt: "clamp(0.25rem, 1vh, 0.5rem)",
                                }}
                            >
                                {t('dashboard:home-title')}
                            </Typography>
                        </>
                    )}

                    <Box
                        sx={{
                            width: "100%",
                            minHeight: isRoleSelectionActive ? "40rem" : "auto",
                            backgroundColor: isRoleSelectionActive ? "transparent" : "#FFFFFF",
                            border: isRoleSelectionActive ? "none" : `1px solid ${colors.layout_border || "#E3E8E3"}`,
                            boxShadow: isRoleSelectionActive ? "none" : "0px 12px 24px rgba(0,0,0,0.05)",
                            borderRadius: "2rem",
                            transition: "all 0.3s ease-in-out",
                            mt: { xs: '1.5rem', sm: 'clamp(1rem, 4vh, 2.5rem)' },
                            px: { xs: '1.5rem', sm: '2rem' },
                            // Equal top and bottom padding using consistent spacing token
                            pt: { xs: '1.5rem', sm: '2rem' },
                            pb: { xs: '1.5rem', sm: '2rem' },
                        }}
                    >
                        <DialogContent sx={{ p: 0, overflow: "visible", width: '100%' }}>
                            <SignUp
                                onClose={() => setLoginOpen(false)}
                                onRoleSelectionChange={setIsRoleSelectionActive}
                            />
                        </DialogContent>
                    </Box>

                    {!isRoleSelectionActive && (
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                mt: { xs: 3, sm: 4 },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    lineHeight: "20px",
                                    fontWeight: 400,
                                    color: colors.text || "#6D786D",
                                    fontFamily: fonts.poppins,
                                    textAlign: "center",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: "4px"
                                }}
                            >
                                {t('common:byContinuing')}
                                <span
                                    style={{ color: colors.primary, cursor: "pointer", textDecoration: "underline" }}
                                    onClick={() => navigate("/terms-conditions")}
                                >
                                    {t('common:terms')}
                                </span>
                                {t('common:and')}
                                <span
                                    style={{ color: colors.primary, cursor: "pointer", textDecoration: "underline" }}
                                    onClick={() => navigate("/privacy-policy")}
                                >
                                    {t('common:privacy')}
                                </span>
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Dialog>
        </Box>
    );
});

export default NewHeaderNavWithSidebar;
